import React, { createContext, useContext, useState, useEffect } from 'react';
import { api, User } from '../services/api';

interface UserContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: any) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  updateUser: (userData: Partial<User>) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in on app start
    const initializeUser = () => {
      try {
        const currentUser = api.getCurrentUser();
        const isAuth = api.isAuthenticated();
        
        if (currentUser && isAuth) {
          setUser(currentUser);
        }
      } catch (error) {
        console.error('Error initializing user:', error);
        api.logout(); // Clear invalid data
      } finally {
        setLoading(false);
      }
    };

    initializeUser();
  }, []);

  // Admin pages authenticate against a separate 'adminToken'/'adminUser' pair
  // (see services/adminApi.ts). Mirror the regular session into those keys so
  // admins only ever log in once, through this same form.
  useEffect(() => {
    if (user?.role === 'ADMIN') {
      const token = localStorage.getItem('authToken');
      if (token) {
        localStorage.setItem('adminToken', token);
        localStorage.setItem('adminUser', JSON.stringify(user));
      }
    } else {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
    }
  }, [user]);

  const login = async (email: string, password: string) => {
    try {
      console.log('🔐 Attempting login...');
      const response = await api.login({ email, password });
      console.log('✅ Login successful:', response.user);
      setUser(response.user);
    } catch (error) {
      console.log('❌ Login failed, trying direct backend call...');
      
      // FALLBACK: Direct backend call for mobile
      try {
        const directResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, password }),
        });

        if (!directResponse.ok) {
          const error = await directResponse.json();
          throw new Error(error.error || 'Login failed');
        }

        const result = await directResponse.json();
        
        // Store token in localStorage
        if (result.token) {
          localStorage.setItem('authToken', result.token);
          localStorage.setItem('user', JSON.stringify(result.user));
        }

        console.log('✅ Direct login successful:', result.user);
        setUser(result.user);
      } catch (directError) {
        console.log('❌ Direct login also failed');
        throw error; // Throw original error
      }
    }
  };

  const register = async (userData: any) => {
    try {
      console.log('📝 Attempting register...');
      const response = await api.register(userData);
      console.log('✅ Register successful:', response.user);
      setUser(response.user);
    } catch (error) {
      console.log('❌ Register failed, trying direct backend call...');
      
      // FALLBACK: Direct backend call for mobile
      try {
        const directResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(userData),
        });

        if (!directResponse.ok) {
          const error = await directResponse.json();
          throw new Error(error.error || 'Registration failed');
        }

        const result = await directResponse.json();
        
        // Store token in localStorage
        if (result.token) {
          localStorage.setItem('authToken', result.token);
          localStorage.setItem('user', JSON.stringify(result.user));
        }

        console.log('✅ Direct register successful:', result.user);
        setUser(result.user);
      } catch (directError) {
        console.log('❌ Direct register also failed');
        throw error; // Throw original error
      }
    }
  };

  const logout = () => {
    api.logout();
    setUser(null);
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const value: UserContextType = {
    user,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user && api.isAuthenticated(),
    updateUser
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};